import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import App, { TopLevelErrorBoundary } from './App';
import WorkoutApp from './WorkoutApp';
import FeedbackPage from './screens/FeedbackPage';
import AdminFeedbackPage from './screens/AdminFeedbackPage';
import reportWebVitals from './reportWebVitals';

if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
  });
}

// ─── Firebase ────────────────────────────────────────────────────────────────
// Initialise the Firebase JS SDK (web) and wire Crashlytics alongside Sentry
// for unhandled error capture. Both services run in parallel — Sentry covers
// web, Crashlytics covers native Android crashes + forwarded JS exceptions.
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_FIREBASE_API_KEY) {
  import('firebase/app').then(({ initializeApp, getApps }) => {
    if (!getApps().length) {
      initializeApp({
        apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId:             process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
      });
    }
    // Explicitly enable Crashlytics on native (no-op on web — Sentry handles web errors)
    import('@capacitor-firebase/crashlytics').then(({ FirebaseCrashlytics }) => {
      FirebaseCrashlytics.setEnabled({ enabled: true });
    }).catch(() => {});
  }).catch(() => {});

  // Forward unhandled JS errors to Firebase Crashlytics (native only — no-op on web).
  // Sentry's browserTracingIntegration also captures these independently.
  window.addEventListener('error', (event) => {
    import('./firebase').then(({ recordException }) => recordException(event.error)).catch(() => {});
  });
  window.addEventListener('unhandledrejection', (event) => {
    import('./firebase').then(({ recordException }) => recordException(event.reason)).catch(() => {});
  });
}

function DeleteAccount() {
  React.useEffect(() => {
    window.location.replace('/delete-account.html');
  }, []);
  return null;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<TopLevelErrorBoundary><App /></TopLevelErrorBoundary>} />
        <Route path="/workout" element={<TopLevelErrorBoundary><WorkoutApp /></TopLevelErrorBoundary>} />
        <Route path="/workout/*" element={<TopLevelErrorBoundary><WorkoutApp /></TopLevelErrorBoundary>} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();
